import { useState, useEffect } from 'react';
import { BookOpen, Search, ArrowRight, Clock, ChevronLeft, ChevronRight } from 'lucide-react';

const apiBase = () => (window.themeData?.apiUrl ?? '').replace(/\/$/, '') + '/little-anvi/v1';

const CAT_COLORS = [
  'bg-primary/10 text-primary',
  'bg-accent1/10 text-accent1',
  'bg-accent2/10 text-accent2',
  'bg-accent3/10 text-accent3',
];
const catColor = (slug = '') => CAT_COLORS[slug.length % 4];

function PostCard({ post, featured = false }) {
  const cat      = post.categories?.[0];
  const color    = catColor(cat?.slug ?? '');
  const imgEl    = post.featuredImage
    ? <img src={post.featuredImage.src} alt={post.featuredImage.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
    : <div className="w-full h-full bg-gradient-to-br from-primary/10 to-accent1/10 flex items-center justify-center"><BookOpen className="w-12 h-12 text-primary/30" /></div>;

  if (featured) {
    return (
      <a href={post.permalink} className="bg-white rounded-[32px] overflow-hidden shadow-soft border border-gray-100 grid grid-cols-1 lg:grid-cols-2 group hover:-translate-y-1 transition-all duration-300 block">
        <div className="h-64 lg:h-auto overflow-hidden">{imgEl}</div>
        <div className="p-8 md:p-10 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-primary/10 text-primary text-xs font-black px-3 py-1 rounded-full">⭐ Featured</span>
            {cat && <span className={`text-xs font-black px-3 py-1 rounded-full ${color}`}>{cat.name}</span>}
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-textDark italic mb-3 leading-tight">{post.title}</h2>
          <p className="text-textLight font-bold text-sm leading-relaxed mb-5">{post.excerpt}</p>
          <div className="flex items-center gap-4 mb-6">
            <img src={post.author.avatarUrl} alt={post.author.name} className="w-10 h-10 rounded-full object-cover border-2 border-primary/20" />
            <div>
              <p className="text-sm font-black text-textDark">{post.author.name}</p>
              <p className="text-xs text-textLight font-bold">{post.date} · {post.readTime} min read</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 bg-primary text-white font-black text-sm px-6 py-3 rounded-xl hover:bg-primaryHover transition-colors w-fit shadow-soft">
            Read Article <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </a>
    );
  }

  return (
    <a href={post.permalink} className="bg-white rounded-[24px] overflow-hidden shadow-card border border-gray-100 hover:shadow-soft hover:-translate-y-1 transition-all duration-300 group flex flex-col lg:flex-row block">
      <div className="h-52 lg:h-auto lg:w-72 xl:w-80 flex-shrink-0 overflow-hidden">{imgEl}</div>
      <div className="p-6 md:p-8 flex flex-col justify-center flex-1">
        <div className="flex items-center gap-2 mb-3">
          {cat && <span className={`text-xs font-black px-3 py-1 rounded-full ${color}`}>{cat.name}</span>}
          <span className="flex items-center gap-1 text-xs text-textLight font-bold ml-auto">
            <Clock className="w-3 h-3" /> {post.readTime} min read
          </span>
        </div>
        <h3 className="font-black text-textDark text-lg md:text-xl leading-snug mb-2 italic">{post.title}</h3>
        <p className="text-textLight font-bold text-sm leading-relaxed mb-5">{post.excerpt}</p>
        <div className="flex items-center gap-3 pt-4 border-t border-gray-50">
          <img src={post.author.avatarUrl} alt={post.author.name} className="w-9 h-9 rounded-full object-cover border-2 border-primary/20" />
          <div className="flex-1">
            <p className="text-sm font-black text-textDark">{post.author.name}</p>
            <p className="text-xs text-textLight font-bold">{post.date}</p>
          </div>
          <span className="text-primary hover:text-primaryHover font-black text-sm flex items-center gap-1 transition-colors">
            Read Article <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </a>
  );
}

function Skeleton({ featured = false }) {
  if (featured) return (
    <div className="bg-white rounded-[32px] overflow-hidden shadow-soft border border-gray-100 grid grid-cols-1 lg:grid-cols-2 animate-pulse">
      <div className="h-64 lg:h-auto bg-gray-100" />
      <div className="p-10 space-y-4">
        <div className="flex gap-2"><div className="h-6 w-20 bg-gray-100 rounded-full" /><div className="h-6 w-24 bg-gray-100 rounded-full" /></div>
        <div className="h-8 bg-gray-100 rounded-xl w-3/4" />
        <div className="h-8 bg-gray-100 rounded-xl w-1/2" />
        <div className="h-4 bg-gray-100 rounded-xl w-full" />
        <div className="h-4 bg-gray-100 rounded-xl w-4/5" />
        <div className="h-12 bg-gray-100 rounded-xl w-36 mt-4" />
      </div>
    </div>
  );
  return (
    <div className="bg-white rounded-[24px] overflow-hidden shadow-card border border-gray-100 flex animate-pulse">
      <div className="h-52 w-72 bg-gray-100 flex-shrink-0" />
      <div className="p-8 flex-1 space-y-3">
        <div className="h-5 w-24 bg-gray-100 rounded-full" />
        <div className="h-6 bg-gray-100 rounded-xl w-3/4" />
        <div className="h-4 bg-gray-100 rounded-xl w-full" />
        <div className="h-4 bg-gray-100 rounded-xl w-4/5" />
      </div>
    </div>
  );
}

export default function BlogListPage({ initialCategory = '' }) {
  const [posts,      setPosts]      = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search,     setSearch]     = useState('');
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetch(`${apiBase()}/blog-categories`)
      .then(r => r.json()).then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams({ per_page: '9', page: String(page) });
    if (search)          p.set('search', search);
    if (activeCategory)  p.set('category', activeCategory);
    fetch(`${apiBase()}/posts?${p}`)
      .then(r => r.json())
      .then(d => { setPosts(d.posts ?? []); setTotalPages(d.totalPages ?? 1); })
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [search, activeCategory, page]);

  function handleSearch(e) {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  }

  function selectCategory(slug) {
    setActiveCategory(prev => prev === slug ? '' : slug);
    setPage(1);
  }

  const featuredPost = posts.find(p => p.featured) ?? posts[0];
  const listPosts    = featuredPost ? posts.filter(p => p.id !== featuredPost.id) : posts;

  return (
    <div className="pb-20">
      {/* ── Hero ── */}
      <section className="w-full bg-gradient-to-br from-primary/10 via-accent1/10 to-secondary/20 py-16 md:py-20 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-primary/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-accent1/10 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-5 py-2 shadow-soft mb-6 border border-primary/10">
            <BookOpen className="w-4 h-4 text-primary" />
            <span className="text-primary font-bold text-sm">Tips, Stories & Play Ideas</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-textDark italic mb-4">
            Our <span className="text-primary">Blog</span> <span className="text-secondary">📚</span>
          </h1>
          <p className="text-textLight font-bold text-base md:text-lg max-w-xl mx-auto">
            Discover parenting tips, toy reviews, play ideas, and fun activities for your little ones!
          </p>
          <form onSubmit={handleSearch} className="mt-8 max-w-lg mx-auto">
            <div className="flex items-center bg-white rounded-2xl px-5 py-3 shadow-soft border border-gray-100 gap-3">
              <Search className="w-4 h-4 text-primary flex-shrink-0" />
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search articles..."
                className="flex-1 bg-transparent border-none outline-none font-bold text-textDark placeholder:text-gray-400 text-sm"
              />
              <button type="submit" className="bg-primary text-white px-4 py-2 rounded-xl text-sm font-black hover:bg-primaryHover transition-colors">
                Search
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* ── Category filters ── */}
      {categories.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => selectCategory('')}
              className={`px-5 py-2 rounded-full font-black text-sm transition-colors ${!activeCategory ? 'bg-primary text-white shadow-soft' : 'bg-white text-textLight border border-gray-100 hover:border-primary hover:text-primary shadow-card'}`}
            >All Posts</button>
            {categories.map(cat => (
              <button
                key={cat.slug}
                onClick={() => selectCategory(cat.slug)}
                className={`px-5 py-2 rounded-full font-black text-sm transition-colors ${activeCategory === cat.slug ? 'bg-primary text-white shadow-soft' : 'bg-white text-textLight border border-gray-100 hover:border-primary hover:text-primary shadow-card'}`}
              >{cat.name}</button>
            ))}
          </div>
        </section>
      )}

      {/* ── Featured post ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        {loading ? <Skeleton featured /> : featuredPost ? <PostCard post={featuredPost} featured /> : null}
      </section>

      {/* ── Post list ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <div className="flex items-center justify-between mb-8">
          <h3 className="text-xl md:text-2xl font-black text-textDark italic">Latest <span className="text-primary">Articles</span></h3>
          {!loading && <p className="text-sm text-textLight font-bold">{listPosts.length} article{listPosts.length !== 1 ? 's' : ''}</p>}
        </div>

        {loading ? (
          <div className="flex flex-col gap-6">{[1,2,3].map(i => <Skeleton key={i} />)}</div>
        ) : listPosts.length === 0 && !featuredPost ? (
          <div className="text-center py-20 bg-white rounded-[32px] border border-gray-100 shadow-sm">
            <BookOpen className="w-16 h-16 text-primary/30 mx-auto mb-4" />
            <h3 className="text-xl font-black text-textDark mb-2 italic">No articles found</h3>
            <p className="text-textLight font-bold text-sm">Try a different search or category.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {listPosts.map(post => <PostCard key={post.id} post={post} />)}
          </div>
        )}
      </section>

      {/* ── Pagination ── */}
      {!loading && totalPages > 1 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 flex justify-center items-center gap-3">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-textLight hover:border-primary hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm">
            <ChevronLeft className="w-4 h-4" />
          </button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)}
              className={`w-10 h-10 rounded-full font-black text-sm transition-colors ${p === page ? 'bg-primary text-white shadow-soft' : 'bg-white border border-gray-200 text-textLight hover:border-primary hover:text-primary shadow-sm'}`}>
              {p}
            </button>
          ))}
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
            className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-textLight hover:border-primary hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm">
            <ChevronRight className="w-4 h-4" />
          </button>
        </section>
      )}
    </div>
  );
}
